import ApexCharts from 'apexcharts';
import { formatCurrencyUSD } from './funcs.js';

const PURPOSE_ALIASES = {
  manualBuy: 'openLong',
  buySpot: 'openLong',
  manualSell: 'closeLong',
  sellSpot: 'closeLong',
  hedgeSell: 'openShort',
  openHedge: 'openShort',
  hedgeBuy: 'closeShort',
  closeHedge: 'closeShort',
};

export function canonicalPurpose(purpose) {
  return PURPOSE_ALIASES[purpose] || purpose;
}

export const CLOSE_LONG_PURPOSES = new Set(['closeLong', 'manualSell', 'sellSpot']);
export const CLOSE_SHORT_PURPOSES = new Set(['closeShort', 'hedgeBuy', 'closeHedge']);
export const CLOSE_PURPOSES = new Set([...CLOSE_LONG_PURPOSES, ...CLOSE_SHORT_PURPOSES]);
export const OPEN_LONG_PURPOSES = new Set(['openLong', 'manualBuy', 'buySpot']);
export const OPEN_SHORT_PURPOSES = new Set(['openShort', 'hedgeSell', 'openHedge']);
export const OPEN_PURPOSES = new Set([...OPEN_LONG_PURPOSES, ...OPEN_SHORT_PURPOSES]);
export const SIBLING_PURPOSES = new Set([...CLOSE_PURPOSES, ...OPEN_PURPOSES]);

export const AMPLITUDE_SERIES = 'Amplitude';
export const VOL_PER_AMP_SERIES = 'Vol / Amp';
export const MOVEMENT_CHART_ID = 'evol-manual-movement';
export const OPEN_CHART_ID = 'evol-manual-open';
export const VISIBLE_CATEGORY_COUNT = 10;

export function readLogTimestamp(log) {
  if (!log) return null;
  const orderData = log.orderData || log || {};
  let ts = orderData.timestamp || log.processedAt || log.timestamp;
  if (!ts) return null;
  if (typeof ts === 'number' && ts < 10000000000) ts *= 1000;
  const ms = new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function extractLogDay(log) {
  if (!log) return null;
  const orderData = log.orderData || log || {};
  let ts = orderData.timestamp || log.processedAt || log.timestamp;
  if (!ts) return null;
  if (typeof ts === 'number' && ts < 10000000000) ts *= 1000;

  if (log.date && typeof log.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(log.date)) {
    const [, month, day] = log.date.split('-');
    return { dayKey: log.date, label: `${day}/${month}` };
  }

  const date = new Date(ts);
  if (isNaN(date.getTime())) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return { dayKey: `${year}-${month}-${day}`, label: `${day}/${month}` };
}

export function readLogAmplitude(log) {
  const amplitude = Number(log?.marketData?.amplitude);
  return Number.isFinite(amplitude) ? amplitude : null;
}

// Same USD the close and open charts plot: price × qty, first filled field wins.
function readOrderPriceQty(log) {
  const orderData = log?.orderData || log || {};
  const price = Number(
    orderData.price ||
    orderData.execPrice ||
    orderData.avgPrice ||
    log?.price ||
    0
  );
  const qty = Number(
    orderData.qty ||
    orderData.executedQty ||
    orderData.totalFilledQty ||
    log?.qty ||
    0
  );
  return { price, qty };
}

export function readOrderNotional(log) {
  const { price, qty } = readOrderPriceQty(log);
  return price * qty;
}

export function readOrderQty(log) {
  return readOrderPriceQty(log).qty;
}

export function readEventPnl(log) {
  return Number(log?.metrics?.pnl || 0);
}

export function volumePerAmplitude(volumeUsd, amplitude) {
  if (!Number.isFinite(volumeUsd) || !Number.isFinite(amplitude) || amplitude === 0) return null;
  return parseFloat((volumeUsd / amplitude).toFixed(2));
}

export function hideZeroDataLabel(val) {
  if (val == null || Number(val) === 0) return '';
  return val;
}

export function axisMaxFromValues(values, fallback = 5) {
  const finite = (values || []).filter(v => v != null && Number.isFinite(v));
  if (!finite.length) return fallback;
  return Math.max(1, Math.ceil(Math.max(...finite) * 1.2));
}

export function buildRightAxisYAxes(rightAxisMetric, amplitudeMax, volPerAmpMax) {
  return [
    {
      seriesName: AMPLITUDE_SERIES,
      opposite: true,
      show: rightAxisMetric === 'amplitude',
      min: 0,
      max: amplitudeMax,
      tickAmount: 4,
      forceNiceScale: false,
      title: { text: 'Amplitude (%)' },
      labels: { formatter: (v) => `${Number(v || 0).toFixed(1)}%` },
    },
    {
      seriesName: VOL_PER_AMP_SERIES,
      opposite: true,
      show: rightAxisMetric === 'volPerAmp',
      min: 0,
      max: volPerAmpMax,
      tickAmount: 4,
      forceNiceScale: false,
      title: { text: 'USD / Amp' },
      labels: { formatter: formatCurrencyUSD },
    },
  ];
}

export function manualChartPanOptions() {
  return {
    zoom: {
      enabled: true,
      type: 'x',
      allowMouseWheelZoom: true,
    },
    toolbar: {
      show: true,
      autoSelected: 'pan',
      tools: {
        download: false,
        selection: false,
        zoom: false,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
      },
    },
  };
}

export function categoryXAxisOptions(labels, visible = VISIBLE_CATEGORY_COUNT) {
  const options = {
    type: 'category',
    categories: labels,
    tickPlacement: 'on',
    labels: { rotate: -45 },
  };
  if ((labels?.length || 0) > visible) {
    options.range = visible;
  }
  return options;
}

export function latestCategoryZoom(labelCount, visible = VISIBLE_CATEGORY_COUNT) {
  if (!labelCount || labelCount <= visible) return null;
  return {
    start: labelCount - visible,
    end: labelCount,
  };
}

const SIBLING_CHART_IDS = {
  [MOVEMENT_CHART_ID]: OPEN_CHART_ID,
  [OPEN_CHART_ID]: MOVEMENT_CHART_ID,
};

let syncingXWindow = false;

export function syncSiblingXWindow(sourceId, xaxis) {
  if (syncingXWindow || !sourceId || xaxis?.min == null || xaxis?.max == null) return;
  const targetId = SIBLING_CHART_IDS[sourceId];
  if (!targetId) return;

  syncingXWindow = true;
  try {
    ApexCharts.exec(targetId, 'zoomX', xaxis.min, xaxis.max);
  } catch (err) {
    // Sibling may not be mounted yet.
  } finally {
    setTimeout(() => {
      syncingXWindow = false;
    }, 0);
  }
}

function chartFromContext(component, chartContext) {
  return chartContext
    || component.$refs?.chart?.chart
    || component.$refs?.chart
    || null;
}

export const rightAxisMetricMixin = {
  data() {
    return {
      rightAxisMetric: 'amplitude',
    };
  },
  watch: {
    rightAxisMetric() {
      this.$nextTick(() => this.syncRightAxisSeries());
    },
    dailyBuckets() {
      this.$nextTick(() => {
        this.syncRightAxisSeries();
        this.applyLatestXWindow();
      });
    },
  },
  methods: {
    applyLatestXWindow(chartContext) {
      const chart = chartFromContext(this, chartContext);
      const zoom = latestCategoryZoom(this.dailyBuckets?.labels?.length || 0);
      if (!chart || !zoom || typeof chart.zoomX !== 'function') return;
      chart.zoomX(zoom.start, zoom.end);
    },
    latestResetZoomWindow() {
      const zoom = latestCategoryZoom(this.dailyBuckets?.labels?.length || 0);
      if (!zoom) return undefined;
      return { xaxis: { min: zoom.start, max: zoom.end } };
    },
    onSiblingXWindow(chartContext, { xaxis } = {}) {
      const sourceId = chartContext?.w?.config?.chart?.id;
      syncSiblingXWindow(sourceId, xaxis);
    },
    syncRightAxisSeries(chartContext) {
      const chart = chartFromContext(this, chartContext);
      if (!chart || typeof chart.hideSeries !== 'function') return;

      if (this.rightAxisMetric === 'amplitude') {
        chart.showSeries(AMPLITUDE_SERIES);
        chart.hideSeries(VOL_PER_AMP_SERIES);
      } else {
        chart.showSeries(VOL_PER_AMP_SERIES);
        chart.hideSeries(AMPLITUDE_SERIES);
      }
    },
    handleRightAxisLegendClick(chartContext, seriesIndex, opts) {
      const name = opts?.config?.series?.[seriesIndex]?.name
        || opts?.globals?.seriesNames?.[seriesIndex];
      if (name === AMPLITUDE_SERIES) {
        this.rightAxisMetric = 'amplitude';
      } else if (name === VOL_PER_AMP_SERIES) {
        this.rightAxisMetric = 'volPerAmp';
      } else {
        return;
      }

      const apply = () => this.syncRightAxisSeries(chartContext);
      this.$nextTick(apply);
      setTimeout(apply, 0);
    },
  },
};
